import { styled } from '@mui/material';

import { Link, useParams } from 'react-router';

import { useState } from 'react';

import { formatDisplayDate } from '@app-velocity/common';

import { TPrototypeShortDto } from '@app-velocity/av-api-v2';
import {
  useGetSharedPrototypeFullSummaryQuery,
  useGetScenarioReviewsByShareTokenQuery,
  useSharedScenarioEvents,
  useGetScenarioQuery,
} from '@app-velocity/modules/scenarios';
import { ButtonsContainer, Card, Divider, Skeleton } from '@app-velocity/ui';

import {
  ArrowDownIcon,
  FigmaOutlineIcon,
  FileIcon,
  PrototypesIcon,
} from '@app-velocity/icons';

import { ReportSettings } from './components/ReportSettings';
import { useSingleReport } from './hooks/userSingleReport';
import { RemoveFigmaReportButton } from '../RemoveFigmaReportButton';
import { usePrototypeStatistics } from '../../../../hooks/usePrototypeStatistics';
import { SimulationReportRoutesPaths } from '../../../../routes';
import { SingleReportRunDetails } from './components/SingleReportRunDetails';
import { MoveFigmaReportButton } from '../MoveFigmaReportButton';

export type SingleReportCardProps = {
  prototype: TPrototypeShortDto;
};

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(4),
  border: '1px solid var(--stroke-stroke-primary-color)',
  boxShadow: `0  ${theme.spacing(0.5)} ${theme.spacing(1)} ${theme.spacing(
    -0.5
  )} rgba(0,0,0,0.1)`,

  borderRadius: theme.spacing(3),
  background: '#fff',
}));

const Name = styled(Link)(({ theme }) => ({
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  fontSize: 14,
  '&:link,&:visited,&:active': {
    color: 'var(--text-text-primary-color)',
  },
  '&:hover': {
    color: 'var(--text-text-primary-color)',
    textDecoration: 'underline',
  },
  svg: {
    fill: 'var(--text-text-primary-color)',
  },
}));

const Right = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginLeft: 'auto',
}));

const Date = styled('span')(({ theme }) => ({
  fontSize: 12,
  color: 'var(--text-text-secondary-color)',
  fontWeight: 400,
  svg: {
    fill: 'var(--text-text-secondary-color)',
  },
}));

const Details = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  fontWeight: 700,
  marginLeft: 'auto',
  cursor: 'pointer',
}));

const Prompt = styled('div')(({ theme }) => ({
  borderRadius: theme.spacing(2),
  background: 'var(--background-background-secondary-color)',
  padding: theme.spacing(2),
  fontSize: 12,
}));

const ExpandButton = styled('button')(({ theme }) => ({
  padding: `${theme.spacing(2.5)} ${theme.spacing(2.5)}`,
  textAlign: 'center',
  color: 'var(--button-button-secondary-text-color)',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  svg: {
    fill: 'var(--button-button-secondary-text-color)',
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
}));

const RotatedArrow = styled('div')({
  transform: 'rotate(-90deg)',
  display: 'flex',
});

const Bottom = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(4),
  padding: `${theme.spacing(2)} 0`,
}));

const Meta = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const SingleMeta = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  color: 'var(--text-text-tertiary-color)',
  fontSize: 11,
  svg: {
    flex: '0 0 auto',
    width: theme.spacing(3.5),
    height: theme.spacing(3.5),
    fill: 'var(--text-text-tertiary-color)',
  },
}));

const Additional = styled('div')<{ visible: boolean }>(({ visible }) => ({
  maxHeight: visible ? 400 : 0,
  opacity: visible ? 1 : 0,
  transition: 'all .5s',
  overflow: 'auto',
}));

export const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: `${theme.spacing(2)} 0`,
}));

export const SingleReportCard = ({ prototype }: SingleReportCardProps) => {
  const { canManageReport } = useSingleReport(prototype);
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [expanded, setExpanded] = useState(false);

  const toggle = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setExpanded((prev) => !prev);
  };

  const { data: scenario, isLoading: isLoadingScenario } = useGetScenarioQuery(
    workspaceId,
    prototype.id || ''
  );

  const { data: summary, isLoading: isSummaryLoading } =
    useGetSharedPrototypeFullSummaryQuery(prototype.shareToken);

  const { data: humanReviews, isLoading: areHumanReviewsLoading } =
    useGetScenarioReviewsByShareTokenQuery(prototype.shareToken || '');

  const { data: humanEvents } = useSharedScenarioEvents(
    prototype.shareToken || ''
  );

  const statistics = usePrototypeStatistics(
    scenario,
    summary,
    humanReviews || undefined,
    humanEvents
  );

  const detailsLoading =
    !statistics ||
    isSummaryLoading ||
    areHumanReviewsLoading ||
    isLoadingScenario;

  return (
    <StyledCard>
      <Name to={SimulationReportRoutesPaths.goToReport(prototype.shareToken!)}>
        <PrototypesIcon /> {prototype.name}
      </Name>
      <StyledDivider />

      <Details onClick={toggle} role="button">
        {detailsLoading ? (
          <Skeleton height={35} margin="0" borderRadius={2} flex={1} />
        ) : (
          <SingleReportRunDetails statistics={statistics} />
        )}
        <Right>
          {prototype.created ? (
            <Date>{formatDisplayDate(prototype.created)}</Date>
          ) : null}
          <ExpandButton>
            {expanded ? (
              <ArrowDownIcon />
            ) : (
              <RotatedArrow>
                <ArrowDownIcon />
              </RotatedArrow>
            )}
          </ExpandButton>
        </Right>
      </Details>

      <Additional visible={expanded}>
        <StyledDivider />
        <Prompt>{prototype.introMessage}</Prompt>
        <Bottom>
          <Meta>
            <SingleMeta>
              <FileIcon /> {prototype.pageName}
            </SingleMeta>
            <SingleMeta>
              <FigmaOutlineIcon /> {prototype.fileName}
            </SingleMeta>
          </Meta>
          <ReportSettings prototype={prototype} />
        </Bottom>
        {canManageReport ? (
          <>
            <StyledDivider />
            <ButtonsContainer>
              <RemoveFigmaReportButton id={prototype.id!} />
              <MoveFigmaReportButton id={prototype.id!} />
            </ButtonsContainer>
          </>
        ) : null}
      </Additional>
    </StyledCard>
  );
};
